import { type JSX, Show } from 'solid-js';
import { t, tPlural } from '../i18n';
import { parseHygieneSummaryEvent, parseRunStageEvent, type RunEventRow } from '../lib/fleetStream';
import { absolute } from '../lib/time';
import { RunEventFacts } from './RunEventFacts';
import { RunEventTitle } from './RunEventTitle';
import { runEventNodeId } from './RunOutcomeBanner';
import { StatusPill, type StatusTone } from './StatusPill';

// One `<li>` of the run timeline: title, meta line, outcome pill, body. Lifted
// out of <FleetRunTimeline/> when that file grew a filter bar; the title rules
// live one level further out again (<RunEventTitle/>).
//
// The node carries an `id` so the outcome banner's "jump to the error" has an
// anchor, and `data-kind` so the timeline's noise filter is expressible in tests
// without reading translated copy.

const OUTCOME_TONE: Record<RunEventRow['outcome'], StatusTone> = {
  success: 'success',
  failure: 'danger',
  skipped: 'neutral',
};

const OUTCOME_LABEL: Record<RunEventRow['outcome'], string> = {
  success: 'fleet.outcome_success',
  failure: 'fleet.outcome_failure',
  skipped: 'fleet.outcome_skipped',
};

const GIB = 1024 ** 3;

// "from {from}" transition note, shown only when the payload names the prior
// stage — the first stage a run enters has none.
function eventTransition(ev: RunEventRow): string | null {
  const stageEvent = parseRunStageEvent(ev);
  if (!stageEvent?.from) return null;
  const key = `fleet.stage_${stageEvent.from}`;
  const from = t.has(key) ? t(key) : stageEvent.from;
  return t('fleet.stage_transition', { from });
}

// A `hygiene_summary` sub-event is a fleet FINDING, not a status blip — counts +
// bytes reclaimed inline, so a maintainer reads "3 findings, 1 action, freed 1.20
// GiB" without opening the payload. `enabled: false` says so plainly rather than
// showing a zero-count line that would read as "ran clean".
function hygieneMeta(ev: RunEventRow): string | null {
  const s = parseHygieneSummaryEvent(ev);
  if (!s) return null;
  if (!s.enabled) return t('fleet.hygiene_disabled');
  const parts = [
    tPlural('fleet.hygiene_findings', s.findings.length, { count: s.findings.length }),
    tPlural('fleet.hygiene_actions', s.actions.length, { count: s.actions.length }),
  ];
  if (s.bytesFreed > 0) {
    parts.push(t('fleet.hygiene_freed', { gib: (s.bytesFreed / GIB).toFixed(2) }));
  }
  if (s.errors.length > 0) {
    parts.push(tPlural('fleet.hygiene_errors', s.errors.length, { count: s.errors.length }));
  }
  if (s.dryRun) parts.push(t('fleet.hygiene_dry_run'));
  return parts.join(' · ');
}

/** The meta-line note: a hygiene finding's counts, else a stage transition, else nothing. */
function eventDetail(ev: RunEventRow): string | null {
  return hygieneMeta(ev) ?? eventTransition(ev);
}

export type RunEventNodeProps = {
  readonly event: RunEventRow;
};

export function RunEventNode(props: RunEventNodeProps): JSX.Element {
  return (
    <li
      class="timeline__item fleet-timeline__node"
      id={runEventNodeId(props.event.seq)}
      data-outcome={props.event.outcome}
      data-kind={props.event.kind}
    >
      <div class="timeline__summary">
        <span class="timeline__dot" aria-hidden="true" />
        <div>
          <RunEventTitle event={props.event} />
          <p class="timeline__meta">
            #{props.event.seq} ·{' '}
            <time datetime={props.event.created_at_iso}>
              {absolute(props.event.created_at_iso)}
            </time>
            <Show when={eventDetail(props.event)}>{(detail) => <> · {detail()}</>}</Show>
          </p>
        </div>
        <StatusPill tone={OUTCOME_TONE[props.event.outcome]}>
          {t(OUTCOME_LABEL[props.event.outcome])}
        </StatusPill>
      </div>
      <RunEventFacts event={props.event} />
    </li>
  );
}
